import { forwardRef } from "react";
import Typography from "../Typography";
import { type VariantProps, cva, cx } from "cva";
import type { ComponentProps, ReactNode } from "react";
import type { BadgeColor, BadgeSize, BadgeVariant } from "./Badge.types.ts";

const badgeRecipe = cva({
  base: "kn-badge",
  variants: {
    color: {
      dark: "kn-badge-dark",
      primary: "kn-badge-primary",
      secondary: "kn-badge-secondary",
      tertiary: "kn-badge-tertiary",
    } satisfies Record<BadgeColor, string>,
    size: {
      small: "kn-badge-small",
      large: "kn-badge-large",
    } satisfies Record<BadgeSize, string>,
    variant: {
      square: "kn-badge-square",
      rounded: "kn-badge-rounded",
    } satisfies Record<BadgeVariant, string>,
  },
  defaultVariants: {
    color: "primary",
    size: "large",
    variant: "square",
  },
});

const outlinedBadgeRecipe = cva({
  base: "kn-badge-outlined",
  variants: {
    color: {
      primary: "kn-badge-primary",
      secondary: "kn-badge-secondary",
      tertiary: "kn-badge-tertiary",
      dark: "kn-badge-dark",
    } satisfies Record<BadgeColor, string>,
  },
  defaultVariants: {
    color: "primary",
  },
});

type BadgeRecipeVariants = VariantProps<typeof badgeRecipe>;

// Define the props for the Badge component
export type BadgeProps = ComponentProps<"div"> &
  BadgeRecipeVariants & {
    outlined?: boolean;
    textClassName?: string;
    left?: ReactNode;
    leftClassName?: string;
    right?: ReactNode;
    rightClassName?: string;
  };

// Forward the ref and define the Badge component
const Badge = forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      color = "primary",
      size = "large",
      variant = "square",
      outlined = false,
      left,
      right,
      className,
      textClassName,
      leftClassName,
      rightClassName,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <Typography
        {...props}
        variant="reportLabelBold"
        ref={ref}
        className={cx(
          badgeRecipe({ color, size, variant }),
          outlined && outlinedBadgeRecipe({ color }),
          className,
        )}
      >
        {/* Render the left icon container */}
        {left ? (
          <div className={cx("flex items-center justify center mr-1", leftClassName)}>{left}</div>
        ) : null}

        {/* Render the badge text */}
        <div className={textClassName}>{children}</div>

        {/* Render the right icon container */}
        {right ? (
          <div className={cx("flex items-center justify center ml-1", rightClassName)}>{right}</div>
        ) : null}
      </Typography>
    );
  },
);

// Set the display name for the Badge component
Badge.displayName = "Badge";

export default Badge;
